import { useCallback } from "react";

import { useBlock } from "@/components/PowerBlock/Block/block-store";
import { Entity, EntityType } from "@/queries";
import { useGetWorkflowIdByName } from "@/queries/workflows";
import useWorkflowVersion from "@/queries/workflows/useWorkflowVersion";
import { WorkflowConfiguration } from "@/types/evaluate";

export const useWorkflow = () => {
  const blockStore = useBlock<WorkflowConfiguration>();

  const { data: workflowIdData } = useGetWorkflowIdByName(blockStore.configuration?.name);

  const version = useWorkflowVersion({
    workflowName: blockStore.configuration?.name,
    label: blockStore.configuration?.workflow_label_name,
    number: blockStore.configuration?.workflow_version_number,
  });

  const inputVariables = Object.keys(version.data?.required_input_variables ?? {});

  const hasError = version.error;
  const folderId = workflowIdData?.workflow?.folder_id;

  const handleWorkflowSelection = useCallback(
    (entity: Entity<EntityType.WORKFLOW>) => {
      try {
        if (
          !entity.id ||
          !entity.name ||
          typeof entity.id !== "number" ||
          typeof entity.name !== "string"
        ) {
          // eslint-disable-next-line no-console
          console.warn("Invalid workflow selection data:", entity);
          return;
        }

        blockStore.setConfiguration({
          ...blockStore.configuration,
          name: entity.name,
          workflow_id: entity.id,
          workflow_label_name: entity.label,
          workflow_version_number: entity.version_number,
          metadata: blockStore.configuration?.metadata || {},
          input_variables: blockStore.configuration?.input_variables || {},
          return_all_outputs: blockStore.configuration?.return_all_outputs,
        });

        // Only update name if it's still the default or empty
        if (blockStore.isDefaultName || !blockStore.name || blockStore.name.trim() === "") {
          // Check if no other blocks/allSources have the same name as workflow
          if (blockStore.allSources) {
            const allExistingNames: string[] = [];
            Object.values(blockStore.allSources).forEach(sourceArray => {
              allExistingNames.push(...sourceArray);
            });

            const nameExists = allExistingNames.includes(entity.name);
            if (!nameExists) {
              blockStore.setName(entity.name);
              // Keep isDefaultName as true since this is automatic
            }
          } else if (!blockStore.name || blockStore.name.trim() === "") {
            // If no allSources available and block name is empty, use workflow name
            blockStore.setName(entity.name);
            // Keep isDefaultName as true since this is automatic
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error handling workflow selection:", error);
      }
    },
    [blockStore],
  );

  const handleVerboseChange = useCallback(
    (enabled: boolean) => {
      try {
        if (typeof enabled !== "boolean") {
          // eslint-disable-next-line no-console
          console.warn("Invalid verbose mode value:", enabled);
          return;
        }

        blockStore.setConfiguration({
          ...blockStore.configuration,
          verbose: enabled,
        } as WorkflowConfiguration);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error updating verbose mode:", error);
      }
    },
    [blockStore],
  );

  const handleReturnAllOutputsChange = useCallback(
    (enabled: boolean) => {
      try {
        if (typeof enabled !== "boolean") {
          // eslint-disable-next-line no-console
          console.warn("Invalid return all outputs value:", enabled);
          return;
        }

        blockStore.setConfiguration({
          ...blockStore.configuration,
          return_all_outputs: enabled,
        } as WorkflowConfiguration);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error updating return all outputs:", error);
      }
    },
    [blockStore],
  );

  return {
    blockStore,
    workflowId: workflowIdData ? workflowIdData.workflow_id : blockStore.configuration?.workflow_id,
    folderId,
    version,
    inputVariables,
    handleWorkflowSelection,
    handleVerboseChange,
    handleReturnAllOutputsChange,
    hasError,
    isLoading: blockStore.configuration?.name ? version.isLoading : false,
    inputVariablesIsLoading: blockStore.configuration?.name ? version.isLoading : false,
  };
};
